import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { FileText, Upload, Link as LinkIcon, X, Plus, AlertCircle, Loader, ArrowRight, ArrowLeft } from 'lucide-react'
import toast from 'react-hot-toast'
import AuthHeader from '../../components/auth/AuthHeader'
import RegistrationSidebar from '../../components/registration/RegistrationSidebar'
import StepIndicator from '../../components/registration/StepIndicator'
import { vendorService } from '../../services/vendorService'
import { saveToLocalStorage, getFromLocalStorage, removeFromLocalStorage } from '../../utils/localStorage'
import { mapVendorProfileToForm } from '../../utils/dataMapping'

const ProfileBuildingPage = () => {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    companyDescription: {
      short: '',
      long: ''
    },
    industryDomains: [],
    yearsInOperation: '',
    keyTechnologies: [],
    logo: null,
    brandAssets: [],
    websiteUrl: '',
    linkedinPage: '',
    otherSocialHandles: []
  })

  const [errors, setErrors] = useState({})
  const [isUploading, setIsUploading] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isCheckingStatus, setIsCheckingStatus] = useState(true)
  const [userId, setUserId] = useState(null)
  const [vendorId, setVendorId] = useState(null)
  const [isStepCompleted, setIsStepCompleted] = useState(false)

  // Enum options
  const [industryOptions, setIndustryOptions] = useState([])
  const [industryLoading, setIndustryLoading] = useState(true)
  const [technologyOptions, setTechnologyOptions] = useState([])
  const [technologyLoading, setTechnologyLoading] = useState(true)
  const [yearsOfOperationOptions, setYearsOfOperationOptions] = useState([])
  const [yearsOfOperationLoading, setYearsOfOperationLoading] = useState(true)

  // Check registration status on mount
  useEffect(() => {
    console.log('ProfileBuildingPage useEffect triggered')
    const checkRegistrationStatus = async () => {
      try {
        setIsCheckingStatus(true)
        console.log('ProfileBuildingPage - Starting checkRegistrationStatus')
        const user = await vendorService.getCurrentUser()
        console.log('ProfileBuildingPage - User data:', user)
        
        if (!user) {
          toast.error('Please log in to continue')
          navigate('/auth/login')
          return
        }

        const currentUserId = user.id || user.user_id
        setUserId(currentUserId)

        // Get vendorId
        const currentVendorId = user.vendor_id || user.vendorId || sessionStorage.getItem('vendorId')
        if (currentVendorId) {
          setVendorId(currentVendorId)
        } else {
          // Step 1 not completed
          toast.error('Please complete company details first')
          navigate('/vendor/registration/legal-details', { replace: true })
          return
        }

        // Check registration status
        const regStatus = user.hasCompletedRegistration
        const isCompleted = regStatus === true || 
                           regStatus === 'true' || 
                           regStatus === 1 || 
                           regStatus === '1' ||
                           String(regStatus).toLowerCase() === 'true'
        
        // Check admin review status - only allow dashboard if approved
        const adminReview = user.admin_review || user.adminReview
        if (isCompleted && adminReview === 'approved' && user.status === 'active') {
          navigate('/vendor/dashboard', { replace: true })
          return
        }
        
        // Navigation logic based on hasCompletedRegistration:
        // - true → Step 4 (Documents)
        // - "vendor-profile" → Step 3 (Subscription) - should redirect here
        // - "vendor-register" → Step 2 (Profile Building) - current step, allow editing
        // - false → Step 1 (Legal Details) - should redirect here
        
        if (isCompleted) {
          // All steps completed - go to documents
          navigate('/vendor/registration/documents', { replace: true })
          return
        } else if (regStatus === 'vendor-profile' || regStatus === 'vendor_profile') {
          // Step 2 completed - should be on Step 3 (Subscription)
          navigate('/vendor/registration/subscription', { replace: true })
          return
        } else if (regStatus !== 'vendor-register' && regStatus !== 'vendor_register') {
          // Step 1 not completed - should be on Step 1 (Legal Details)
          toast.error('Please complete company details first')
          navigate('/vendor/registration/legal-details', { replace: true })
          return
        }
        
        // User is on correct step (Step 2) - regStatus === 'vendor-register'
        // Load existing data using the new API - force refresh to get latest data
        console.log('Step 2 - currentVendorId:', currentVendorId, 'regStatus:', regStatus)
        setIsStepCompleted(true) // Mark step as completed since they're on step 2
        
        if (currentVendorId) {
          try {
            console.log('Calling getVendorProfileData for vendorId:', currentVendorId, '(force refresh)')
            const response = await vendorService.getVendorProfileData(currentVendorId, true) // Force refresh
            console.log('getVendorProfileData response:', response)
            if (response?.success && response?.data) {
              const mappedData = mapVendorProfileToForm(response.data)
              if (mappedData) {
                // Merge with existing form data (API data takes precedence)
                setFormData(prev => ({
                  ...prev,
                  ...mappedData
                }))
                // Show subtle notification if using cached data
                if (response.cached) {
                  toast('Loaded saved data', { icon: 'ℹ️', duration: 2000 })
                }
              }
            }
          } catch (error) {
            console.error('Error loading profile data:', error)
            // Non-blocking error - allow user to continue with empty form
            toast.error('Could not load saved data. You can continue with a new form.', { duration: 3000 })
          }
        }
      } catch (error) {
        toast.error('Error loading user information')
        navigate('/auth/login')
      } finally {
        setIsCheckingStatus(false)
      }
    }

    checkRegistrationStatus()
  }, [navigate])

  // Load form data from localStorage on mount
  useEffect(() => {
    const savedData = getFromLocalStorage('vendor_registration_step2')
    if (savedData) {
      setFormData(prev => ({ ...prev, ...savedData.formData }))
    }
  }, [])

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    if (formData.companyDescription.short || formData.industryDomains.length > 0 || formData.keyTechnologies.length > 0) {
      saveToLocalStorage('vendor_registration_step2', {
        formData
      })
    }
  }, [formData])

  // Fetch enums
  useEffect(() => {
    let mounted = true

    const fetchEnums = async () => {
      try {
        setIndustryLoading(true)
        setTechnologyLoading(true)
        setYearsOfOperationLoading(true)

        const [industryRes, techRes, yearsRes] = await Promise.all([
          vendorService.getEnum('industries').catch(err => ({ success: false })),
          vendorService.getEnum('technologies').catch(err => ({ success: false })),
          vendorService.getEnum('years_in_operation').catch(err => ({ success: false }))
        ])

        if (mounted) {
          if (industryRes?.success && industryRes.data) {
            setIndustryOptions(industryRes.data.map(i => i.value))
          }
          if (techRes?.success && techRes.data) {
            setTechnologyOptions(techRes.data.map(t => t.value))
          }
          if (yearsRes?.success && yearsRes.data) {
            setYearsOfOperationOptions(yearsRes.data)
          }
        }
      } catch (err) {
        console.error('Error fetching enums:', err)
      } finally {
        if (mounted) {
          setIndustryLoading(false)
          setTechnologyLoading(false)
          setYearsOfOperationLoading(false)
        }
      }
    }

    fetchEnums()
    return () => { mounted = false }
  }, [])

  const validateForm = () => {
    const newErrors = {}

    if (!formData.companyDescription.short.trim()) {
      newErrors.companyDescription = 'Short company description is required'
    }

    if (!formData.companyDescription.long.trim()) {
      newErrors.companyDescription = 'Long company description is required'
    }

    if (formData.industryDomains.length === 0) {
      newErrors.industryDomains = 'Please select at least one industry domain'
    }

    if (!formData.yearsInOperation) {
      newErrors.yearsInOperation = 'Years in operation is required'
    }

    if (formData.keyTechnologies.length === 0) {
      newErrors.keyTechnologies = 'Please select at least one key technology'
    }

    if (formData.websiteUrl && !isValidUrl(formData.websiteUrl)) {
      newErrors.websiteUrl = 'Please enter a valid website URL'
    }

    if (formData.linkedinPage && !isValidLinkedInUrl(formData.linkedinPage)) {
      newErrors.linkedinPage = 'Please enter a valid LinkedIn page URL'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const isValidUrl = (url) => {
    try {
      new URL(url)
      return true
    } catch {
      return false
    }
  }

  const isValidLinkedInUrl = (url) => {
    return url.includes('linkedin.com/company/') || url.includes('linkedin.com/in/')
  }

  const handleChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.')
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }))
    }

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }))
    }
  }

  const handleIndustryDomainToggle = (domain) => {
    setFormData(prev => ({
      ...prev,
      industryDomains: prev.industryDomains.includes(domain)
        ? prev.industryDomains.filter(d => d !== domain)
        : [...prev.industryDomains, domain]
    }))
  }

  const handleSelectAllIndustries = () => {
    setFormData(prev => ({
      ...prev,
      industryDomains: industryOptions
    }))
  }

  const handleDeselectAllIndustries = () => {
    setFormData(prev => ({
      ...prev,
      industryDomains: []
    }))
  }

  const handleTechnologyToggle = (tech) => {
    setFormData(prev => ({
      ...prev,
      keyTechnologies: prev.keyTechnologies.includes(tech)
        ? prev.keyTechnologies.filter(t => t !== tech)
        : [...prev.keyTechnologies, tech]
    }))
  }

  const handleSelectAllTechnologies = () => {
    setFormData(prev => ({
      ...prev,
      keyTechnologies: technologyOptions
    }))
  }

  const handleDeselectAllTechnologies = () => {
    setFormData(prev => ({
      ...prev,
      keyTechnologies: []
    }))
  }

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    const maxSize = 5 * 1024 * 1024 // 5MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/svg+xml']

    if (file.size > maxSize) {
      toast.error('Logo file size must be less than 5MB')
      return
    }

    if (!allowedTypes.includes(file.type)) {
      toast.error('Logo must be a JPG, PNG, or SVG file')
      return
    }

    setIsUploading(true)
    try {
      await new Promise(resolve => setTimeout(resolve, 1000))

      setFormData(prev => ({
        ...prev,
        logo: {
          file,
          name: file.name,
          size: file.size,
          type: file.type,
          url: URL.createObjectURL(file)
        }
      }))

      toast.success('Logo uploaded successfully!')
    } catch (error) {
      toast.error('Failed to upload logo. Please try again.')
    } finally {
      setIsUploading(false)
    }
  }

  const handleBrandAssetsUpload = async (event) => {
    const files = Array.from(event.target.files)
    if (files.length === 0) return

    setIsUploading(true)
    try {
      await new Promise(resolve => setTimeout(resolve, 1000))

      const uploadedFiles = files.map(file => ({
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        url: URL.createObjectURL(file)
      }))

      setFormData(prev => ({
        ...prev,
        brandAssets: [...prev.brandAssets, ...uploadedFiles]
      }))

      toast.success(`${files.length} file(s) uploaded successfully!`)
    } catch (error) {
      toast.error('Failed to upload files. Please try again.')
    } finally {
      setIsUploading(false)
    }
  }

  const removeBrandAsset = (index) => {
    setFormData(prev => ({
      ...prev,
      brandAssets: prev.brandAssets.filter((_, i) => i !== index)
    }))
  }

  const addSocialHandle = () => {
    setFormData(prev => ({
      ...prev,
      otherSocialHandles: [...prev.otherSocialHandles, { platform: '', url: '' }]
    }))
  }

  const updateSocialHandle = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      otherSocialHandles: prev.otherSocialHandles.map((handle, i) =>
        i === index ? { ...handle, [field]: value } : handle
      )
    }))
  }

  const removeSocialHandle = (index) => {
    setFormData(prev => ({
      ...prev,
      otherSocialHandles: prev.otherSocialHandles.filter((_, i) => i !== index)
    }))
  }

  const parseYearsInOperation = (yearsString) => {
    if (!yearsString) return 0
    const yearsMap = {
      '0-1': 0,
      '1-3': 1,
      '3-5': 3,
      '5-10': 5,
      '10-15': 10,
      '15+': 15
    }
    return yearsMap[yearsString] || parseInt(yearsString) || 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!userId || !vendorId) {
      toast.error('User not authenticated. Please log in again.')
      navigate('/auth/login')
      return
    }

    if (!validateForm()) {
      toast.error('Please fix the errors before continuing.')
      return
    }

    try {
      setIsLoading(true)

      const dataToSave = {
        vendor_id: vendorId,
        user_id: userId,
        short_description: formData.companyDescription.short,
        detailed_description: formData.companyDescription.long,
        industry_domains: formData.industryDomains,
        years_in_operation: parseYearsInOperation(formData.yearsInOperation),
        key_technologies: formData.keyTechnologies,
        website_url: formData.websiteUrl,
        linkedin_url: formData.linkedinPage,
        other_social_handles: formData.otherSocialHandles.length > 0
          ? formData.otherSocialHandles.map(handle => `${handle.platform}: ${handle.url}`).join(' | ')
          : '',
        logo: formData.logo ? {
          file: formData.logo.file,
          name: formData.logo.name,
          size: formData.logo.size,
          type: formData.logo.type
        } : null,
        brand_assets: formData.brandAssets.length > 0 ? {
          file: formData.brandAssets[0].file,
          name: formData.brandAssets[0].name,
          size: formData.brandAssets[0].size,
          type: formData.brandAssets[0].type
        } : null
      }

      await vendorService.saveProfileDetails(dataToSave)

      // Update user registration status
      const userDataString = sessionStorage.getItem('userData')
      if (userDataString) {
        const userData = JSON.parse(userDataString)
        userData.hasCompletedRegistration = 'vendor-profile'
        sessionStorage.setItem('userData', JSON.stringify(userData))
      }

      // Clear localStorage after successful submission
      removeFromLocalStorage('vendor_registration_step2')
      
      toast.success('Profile details saved successfully!')
      navigate('/vendor/registration/subscription')
    } catch (error) {
      toast.error(error.message || 'Failed to save profile details. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  if (isCheckingStatus) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 dark:from-secondary-900 dark:via-secondary-800 dark:to-secondary-900 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-10 h-10 mx-auto mb-4 animate-spin text-primary-600" />
          <p className="text-secondary-600 dark:text-secondary-400">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 dark:from-secondary-900 dark:via-secondary-800 dark:to-secondary-900">
      <AuthHeader />

      <div className="flex flex-col items-center min-h-[calc(100vh-64px)] p-4 py-4 gap-4">
        {/* Step Indicator - Outside the white card */}
        <div className="w-full max-w-6xl">
          <StepIndicator currentStep={2} />
        </div>

        <div className="flex items-start justify-center w-full gap-4">
          {/* Sidebar on the left */}
          <RegistrationSidebar currentStep={2} isStepCompleted={isStepCompleted} />
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card w-full max-w-6xl p-5 md:p-6 shadow-glow flex-1"
          >
            {/* Header */}
          <div className="text-center mb-6 pb-6 border-b border-secondary-200 dark:border-secondary-700">
            <h1 className="text-3xl font-display font-bold text-secondary-900 dark:text-secondary-100 mb-2 tracking-tight">
              Profile Setup
            </h1>
            <p className="text-sm text-secondary-600 dark:text-secondary-400 max-w-md mx-auto leading-relaxed">
              Company profile and branding
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Company Description - Full Width */}
            <div className="space-y-3 p-3.5 rounded-lg bg-secondary-50/50 dark:bg-secondary-800/30 border border-secondary-200 dark:border-secondary-700">
              <h3 className="text-sm font-semibold text-secondary-900 dark:text-secondary-100 flex items-center gap-2 pb-1 border-b border-secondary-200 dark:border-secondary-700">
                <FileText className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                Company Description
              </h3>
              <div className="space-y-2.5">
                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                    Short Description (Max 150 chars) <span className="text-error-500">*</span>
                  </label>
                  <textarea
                    value={formData.companyDescription.short}
                    onChange={(e) => handleChange('companyDescription.short', e.target.value)}
                    className="input h-20 text-sm resize-none"
                    maxLength={150}
                    placeholder="Brief description of your company"
                    required
                  />
                  <p className="text-xs text-secondary-500">
                    {formData.companyDescription.short.length}/150 characters
                  </p>
                </div>
                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                    Detailed Description <span className="text-error-500">*</span>
                  </label>
                  <textarea
                    value={formData.companyDescription.long}
                    onChange={(e) => handleChange('companyDescription.long', e.target.value)}
                    className="input h-24 text-sm resize-none"
                    placeholder="Detailed description of your company, services, and expertise"
                    required
                  />
                </div>
              </div>
              {errors.companyDescription && (
                <p className="text-error-500 text-xs mt-0.5 flex items-center gap-1">
                  <span className="w-1 h-1 bg-error-500 rounded-full"></span>
                  {errors.companyDescription}
                </p>
              )}
            </div>

            {/* Company Details - Two Columns */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Left Column */}
              <div className="space-y-4 flex flex-col">
                {/* Years in Operation & Industry Domains */}
                <div className="space-y-3 p-3.5 rounded-lg bg-secondary-50/50 dark:bg-secondary-800/30 border border-secondary-200 dark:border-secondary-700">
                  <h3 className="text-sm font-semibold text-secondary-900 dark:text-secondary-100 flex items-center gap-2 pb-1 border-b border-secondary-200 dark:border-secondary-700">
                    <FileText className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                    Company Details
                  </h3>
                  <div className="space-y-2.5">
                    <div className="space-y-1">
                      <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                        Years in Operation <span className="text-error-500">*</span>
                      </label>
                      <select
                        value={formData.yearsInOperation}
                        onChange={(e) => handleChange('yearsInOperation', e.target.value)}
                        className="input h-9 text-sm"
                        required
                      >
                        <option value="">Select years</option>
                        {yearsOfOperationLoading ? (
                          <option disabled>Loading...</option>
                        ) : yearsOfOperationOptions.length === 0 ? (
                          <option disabled>No options</option>
                        ) : (
                          yearsOfOperationOptions.map(item => (
                            <option key={item.key} value={item.key}>{item.value}</option>
                          ))
                        )}
                      </select>
                      {errors.yearsInOperation && (
                        <p className="text-error-500 text-xs mt-0.5 flex items-center gap-1">
                          <span className="w-1 h-1 bg-error-500 rounded-full"></span>
                          {errors.yearsInOperation}
                        </p>
                      )}
                    </div>

                    {/* Industry Domains */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                          Industry Domains <span className="text-error-500">*</span>
                        </label>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={handleSelectAllIndustries}
                            className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                          >
                            Select All
                          </button>
                          <span className="text-secondary-300">|</span>
                          <button
                            type="button"
                            onClick={handleDeselectAllIndustries}
                            className="text-xs text-secondary-600 hover:text-secondary-700 font-medium"
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-2 max-h-40 overflow-y-auto p-2 border border-secondary-200 dark:border-secondary-700 rounded">
                        {industryLoading ? (
                          <div className="text-xs text-secondary-500">Loading...</div>
                        ) : industryOptions.length === 0 ? (
                          <div className="text-xs text-secondary-500">No industries available</div>
                        ) : (
                          industryOptions.map((domain) => (
                            <label key={domain} className="flex items-center gap-1.5 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.industryDomains.includes(domain)}
                                onChange={() => handleIndustryDomainToggle(domain)}
                                className="w-3.5 h-3.5 border-2 border-secondary-300 dark:border-secondary-600 rounded focus:ring-2 focus:ring-primary-500"
                              />
                              <span className="text-xs text-secondary-700 dark:text-secondary-300">{domain}</span>
                            </label>
                          ))
                        )}
                      </div>
                      {errors.industryDomains && (
                        <p className="text-error-500 text-xs mt-0.5 flex items-center gap-1">
                          <span className="w-1 h-1 bg-error-500 rounded-full"></span>
                          {errors.industryDomains}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4 flex flex-col">
                {/* Key Technologies */}
                <div className="space-y-3 p-3.5 rounded-lg bg-secondary-50/50 dark:bg-secondary-800/30 border border-secondary-200 dark:border-secondary-700">
                  <div className="flex items-center justify-between pb-1 border-b border-secondary-200 dark:border-secondary-700">
                    <h3 className="text-sm font-semibold text-secondary-900 dark:text-secondary-100 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                      Key Technologies <span className="text-error-500">*</span>
                    </h3>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleSelectAllTechnologies}
                        className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                      >
                        Select All
                      </button>
                      <span className="text-secondary-300">|</span>
                      <button
                        type="button"
                        onClick={handleDeselectAllTechnologies}
                        className="text-xs text-secondary-600 hover:text-secondary-700 font-medium"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-2 max-h-40 overflow-y-auto p-2 border border-secondary-200 dark:border-secondary-700 rounded">
                    {technologyLoading ? (
                      <div className="text-xs text-secondary-500">Loading...</div>
                    ) : technologyOptions.length === 0 ? (
                      <div className="text-xs text-secondary-500">No technologies available</div>
                    ) : (
                      technologyOptions.map((tech) => (
                        <label key={tech} className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.keyTechnologies.includes(tech)}
                            onChange={() => handleTechnologyToggle(tech)}
                            className="w-3.5 h-3.5 border-2 border-secondary-300 dark:border-secondary-600 rounded focus:ring-2 focus:ring-primary-500"
                          />
                          <span className="text-xs text-secondary-700 dark:text-secondary-300">{tech}</span>
                        </label>
                      ))
                    )}
                  </div>
                  {errors.keyTechnologies && (
                    <p className="text-error-500 text-xs mt-0.5 flex items-center gap-1">
                      <span className="w-1 h-1 bg-error-500 rounded-full"></span>
                      {errors.keyTechnologies}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Branding & Assets - Two Columns */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Logo Upload */}
              <div className="space-y-3 p-3.5 rounded-lg bg-secondary-50/50 dark:bg-secondary-800/30 border border-secondary-200 dark:border-secondary-700">
                <h3 className="text-sm font-semibold text-secondary-900 dark:text-secondary-100 flex items-center gap-2 pb-1 border-b border-secondary-200 dark:border-secondary-700">
                  <Upload className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                  Company Logo
                </h3>
                <div className="space-y-2.5">
                  {formData.logo ? (
                    <div className="border-2 border-secondary-200 dark:border-secondary-700 rounded-lg p-3 flex items-center gap-3">
                      <img
                        src={formData.logo.url}
                        alt="Company logo"
                        className="w-16 h-16 object-contain border border-secondary-200 dark:border-secondary-700 rounded"
                      />
                      <div className="flex-1">
                        <p className="text-xs font-medium text-secondary-900 dark:text-secondary-100">{formData.logo.name}</p>
                        <p className="text-xs text-secondary-500">
                          {(formData.logo.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, logo: null }))}
                          className="mt-1 text-xs text-error-500 hover:text-error-700"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-secondary-300 dark:border-secondary-600 rounded-lg p-4 text-center">
                      <Upload className="w-6 h-6 text-secondary-400 mx-auto mb-2" />
                      <p className="text-xs text-secondary-600 dark:text-secondary-400 mb-2">Upload logo</p>
                      <p className="text-xs text-secondary-500 mb-3">Max 5MB | JPG, PNG, SVG</p>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/svg+xml"
                        onChange={handleLogoUpload}
                        disabled={isUploading}
                        className="hidden"
                        id="logo-upload"
                      />
                      <label
                        htmlFor="logo-upload"
                        className="btn-outline text-xs px-3 py-1.5 cursor-pointer inline-block"
                      >
                        {isUploading ? 'Uploading...' : 'Choose Logo'}
                      </label>
                    </div>
                  )}
                </div>
              </div>

              {/* Brand Assets */}
              <div className="space-y-3 p-3.5 rounded-lg bg-secondary-50/50 dark:bg-secondary-800/30 border border-secondary-200 dark:border-secondary-700">
                <h3 className="text-sm font-semibold text-secondary-900 dark:text-secondary-100 flex items-center gap-2 pb-1 border-b border-secondary-200 dark:border-secondary-700">
                  <Upload className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                  Brand Assets
                </h3>
                <div className="space-y-2.5">
                  {formData.brandAssets.length > 0 && (
                    <div className="space-y-2">
                      {formData.brandAssets.map((asset, index) => (
                        <div key={index} className="border-2 border-secondary-200 dark:border-secondary-700 rounded-lg p-2 flex items-center gap-2">
                          <img
                            src={asset.url}
                            alt={asset.name}
                            className="w-12 h-12 object-contain border border-secondary-200 dark:border-secondary-700 rounded"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-secondary-900 dark:text-secondary-100 truncate">{asset.name}</p>
                            <p className="text-xs text-secondary-500">
                              {(asset.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeBrandAsset(index)}
                            className="text-error-500 hover:text-error-700 p-1"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="border-2 border-dashed border-secondary-300 dark:border-secondary-600 rounded-lg p-3 text-center">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleBrandAssetsUpload}
                      disabled={isUploading}
                      className="hidden"
                      id="brand-assets-upload"
                    />
                    <label
                      htmlFor="brand-assets-upload"
                      className="btn-outline text-xs px-3 py-1.5 cursor-pointer inline-block"
                    >
                      {isUploading ? 'Uploading...' : 'Add Brand Assets'}
                    </label>
                    <p className="text-xs text-secondary-500 mt-2">Max 5MB per file</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Website & Social Media - Full Width */}
            <div className="space-y-3 p-3.5 rounded-lg bg-secondary-50/50 dark:bg-secondary-800/30 border border-secondary-200 dark:border-secondary-700">
              <h3 className="text-sm font-semibold text-secondary-900 dark:text-secondary-100 flex items-center gap-2 pb-1 border-b border-secondary-200 dark:border-secondary-700">
                <LinkIcon className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                Website & Social Media
              </h3>
              <div className="space-y-2.5">
                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                    Website URL
                  </label>
                  <input
                    type="url"
                    value={formData.websiteUrl}
                    onChange={(e) => handleChange('websiteUrl', e.target.value)}
                    className="input h-9 text-sm"
                    placeholder="https://www.example.com"
                  />
                  {errors.websiteUrl && (
                    <p className="text-error-500 text-xs mt-0.5 flex items-center gap-1">
                      <span className="w-1 h-1 bg-error-500 rounded-full"></span>
                      {errors.websiteUrl}
                    </p>
                  )}
                </div>
                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                    LinkedIn Page URL
                  </label>
                  <input
                    type="url"
                    value={formData.linkedinPage}
                    onChange={(e) => handleChange('linkedinPage', e.target.value)}
                    className="input h-9 text-sm"
                    placeholder="https://www.linkedin.com/company/example"
                  />
                  {errors.linkedinPage && (
                    <p className="text-error-500 text-xs mt-0.5 flex items-center gap-1">
                      <span className="w-1 h-1 bg-error-500 rounded-full"></span>
                      {errors.linkedinPage}
                    </p>
                  )}
                </div>
                {formData.otherSocialHandles.length > 0 && (
                  <div className="space-y-2 pt-2 border-t border-secondary-200 dark:border-secondary-700">
                    <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300">
                      Other Social Handles
                    </label>
                    {formData.otherSocialHandles.map((handle, index) => (
                      <div key={index} className="flex gap-2">
                        <input
                          type="text"
                          value={handle.platform}
                          onChange={(e) => updateSocialHandle(index, 'platform', e.target.value)}
                          className="input h-9 text-sm flex-1"
                          placeholder="Platform (e.g., Twitter)"
                        />
                        <input
                          type="url"
                          value={handle.url}
                          onChange={(e) => updateSocialHandle(index, 'url', e.target.value)}
                          className="input h-9 text-sm flex-1"
                          placeholder="URL"
                        />
                        <button
                          type="button"
                          onClick={() => removeSocialHandle(index)}
                          className="btn-outline h-9 w-9 p-0 flex items-center justify-center"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  onClick={addSocialHandle}
                  className="btn-outline text-xs px-3 py-1.5 flex items-center gap-1.5 w-fit"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Social Handle
                </button>
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between gap-3 pt-4">
              <button
                type="button"
                onClick={() => navigate('/vendor/registration/legal-details')}
                className="btn-outline h-9 text-xs font-semibold flex items-center gap-1.5 px-4"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>Back</span>
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary h-9 text-xs font-semibold shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 px-6"
              >
                {isLoading ? (
                  <>
                    <Loader className="w-3.5 h-3.5 animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <span>Continue to Subscription</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
        </div>
      </div>
    </div>
  )
}

export default ProfileBuildingPage
